import { useEffect, useMemo, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  InFlightTimelineSelector,
  PollingActiveAtom,
  PulseKickAtom,
} from "MVVM/State/clipStatus";
import type { ClipStatusTimeline } from "MVVM/Components/ClipStatus/@types";

type UseInFlightTimelineArgs = {
  trackId?: string;
  shareId?: string;
  /**
   * Restarts the tray's poll if it already went terminal-quiet before this
   * clip existed. Only pass from a single owner per screen, never per card.
   */
  kickWhenIdle?: boolean;
};

/**
 * Reads one clip's pulse timeline off the tray's existing poll. Adds no
 * polling of its own.
 */
export const useInFlightTimeline = ({
  trackId,
  shareId,
  kickWhenIdle = false,
}: UseInFlightTimelineArgs): ClipStatusTimeline | undefined => {
  const params = useMemo(() => ({ trackId, shareId }), [trackId, shareId]);
  const timeline = useRecoilValue(InFlightTimelineSelector(params));
  const pollingActive = useRecoilValue(PollingActiveAtom);
  const setPulseKick = useSetRecoilState(PulseKickAtom);
  const hasKickedRef = useRef(false);

  useEffect(() => {
    if (!kickWhenIdle || pollingActive || timeline || hasKickedRef.current) {
      return;
    }
    hasKickedRef.current = true;
    setPulseKick((n) => n + 1);
  }, [kickWhenIdle, pollingActive, timeline, setPulseKick]);

  return timeline;
};
